import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { MultiMenuTrigger } from '@protonapp/react-multi-menu'
import { toast } from 'react-toastify'
import NewAppModal from 'components/NewApp/Modal'
import addButtonIcon from 'components/Editor/LeftNav/add-button.svg'
import {
  requestApps,
  requestOrgApps,
  requestTeamApps,
  getApps,
  getOrgApps,
  getTeamApps,
  getAppsLoaded,
  deleteApp,
} from 'ducks/apps'
import { addAppUser } from 'ducks/appUsers'
import Loading from 'components/Shared/Loading'
import Button from 'components/Shared/Button'
import Icon from 'components/Shared/Icon'
import Modal from 'components/Shared/Modal'
import CopyAppForm from 'components/Editor/CopyApp/Form'
import { verifyXanoApp } from 'utils/externalDatabases'
import { submitCopyApp } from 'utils/copyApp'
import { getCurrentUser, userIsAdmin } from 'ducks/users/index.ts'
import {
  getCurrentOrganization,
  getCurrentOrganizationId,
  getOrganizations,
  fetchOrganizations,
  fetchOrganization,
  setCurrentOrganization,
} from 'ducks/organizations'

import AppItem from '../../Apps/Item'
import NewDashboardModal from './NewDashboardModal'
import SettingsSidebar from './SettingsSidebar'
import { Account, Team, Billing, Domain } from '../../Settings'

import './Dashboard.css'
import '../../Apps/Apps.css'

const SORT_OPTIONS = {
  EDITED_DESC: 'edited-desc',
  NAME_ASC: 'name-asc',
  NAME_DESC: 'name-desc',
  DATE_DESC: 'date-desc',
  DATE_ASC: 'date-asc',
}

const VIEW_OPTIONS = {
  GRID: 'grid',
  LIST: 'list',
}

const Dashboard = () => {
  const dispatch = useDispatch()
  const apps = useSelector(getApps)
  const orgApps = useSelector(getOrgApps)
  const teamApps = useSelector(getTeamApps)
  const appsLoaded = useSelector(getAppsLoaded)

  // Global user/org data for Settings tabs
  const user = useSelector(getCurrentUser)
  const currentOrganization = useSelector(getCurrentOrganization)
  const currentOrganizationId = useSelector(getCurrentOrganizationId)
  const organizations = useSelector(getOrganizations)

  const settingsLoaded = !!user && !!currentOrganization

  // Check if current user is an org admin
  const isAdmin = useMemo(() => {
    if (!user || !currentOrganization) return false

    return userIsAdmin(currentOrganization, user)
  }, [user, currentOrganization])

  const location = useLocation()

  const getTabParam = search => {
    const params = new URLSearchParams(search)

    return params.get('tab') || 'apps'
  }

  const [query, setQuery] = useState('')
  const [activeTab, setActiveTab] = useState(() => getTabParam(location.search))
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.EDITED_DESC)
  const [viewMode] = useState(VIEW_OPTIONS.LIST)
  const [deletingAppId, setDeletingAppId] = useState(null)

  // Keep activeTab in sync with URL param (e.g., when navigating from navbar menu)
  useEffect(() => {
    const tab = getTabParam(location.search)
    if (tab && tab !== activeTab) {
      setActiveTab(tab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const [isNewAppModalOpen, setNewAppModalOpen] = useState(false)
  const [copyingApp, setCopyingApp] = useState(null)

  useEffect(() => {
    // Fetch apps on first mount
    dispatch(requestApps())

    // Fetch organizations so user/org context is available immediately
    dispatch(fetchOrganizations())
  }, [dispatch])

  // Once we have organizations, ensure a current organization is selected
  useEffect(() => {
    if (!currentOrganization && organizations.length) {
      const defaultOrgId = organizations[0].id
      dispatch(setCurrentOrganization(defaultOrgId))
      dispatch(fetchOrganization(defaultOrgId))
    }
  }, [currentOrganization, organizations, dispatch])

  // Fetch apps when organization is available/changes
  // Use currentOrganizationId (updates immediately on switch) instead of
  // currentOrganization?.id (delayed for admin users until fetchOrganization resolves)
  useEffect(() => {
    if (currentOrganizationId) {
      dispatch(requestApps())
      dispatch(requestOrgApps(currentOrganizationId))

      // Fetch all team apps if user is admin (to show non-member apps)
      if (isAdmin) {
        dispatch(requestTeamApps(currentOrganizationId))
      }
    }
  }, [currentOrganizationId, dispatch, isAdmin])

  // Use apps (flat list) as primary source, filtered by current organization
  const safeApps = useMemo(() => {
    const allApps = Array.isArray(apps) ? apps : []

    if (!currentOrganizationId) return allApps

    return allApps.filter(app => app.OrganizationId === currentOrganizationId)
  }, [apps, currentOrganizationId])

  // Track which apps the user is a member of
  const memberAppIds = useMemo(() => {
    return new Set(safeApps.map(app => app.id))
  }, [safeApps])

  // For admins, merge user's apps with team apps they're not a member of
  const allAppsForDisplay = useMemo(() => {
    if (!isAdmin || !teamApps?.length) return safeApps

    const merged = [...safeApps]

    teamApps.forEach(app => {
      if (!memberAppIds.has(app.id)) {
        merged.push({ ...app, _isMuted: true })
      }
    })

    return merged
  }, [safeApps, teamApps, memberAppIds, isAdmin])

  // Convert orgApps to array of groups for shared database display
  const appGroups = useMemo(() => {
    if (!orgApps) return []

    return Object.values(orgApps)
  }, [orgApps])

  // Filter apps based on search query, expanding to include shared-database siblings
  const filteredApps = useMemo(() => {
    if (!query) {
      return allAppsForDisplay
    }

    const lowerQuery = query.toLowerCase()

    // Step 1: Find direct name matches
    const directMatches = allAppsForDisplay.filter(app =>
      (app.name || '').toLowerCase().includes(lowerQuery)
    )

    // Step 2: Expand to include shared-database siblings
    if (appGroups.length === 0) {
      return directMatches
    }

    const matchedIds = new Set(directMatches.map(app => app.id))
    const allDisplayIds = new Map(allAppsForDisplay.map(app => [app.id, app]))
    const resultIds = new Set(matchedIds)

    // For each matched app, find its group and include all siblings
    for (const group of appGroups) {
      const hasMatch = group.some(app => matchedIds.has(app.id))
      if (hasMatch) {
        for (const app of group) {
          resultIds.add(app.id)
        }
      }
    }

    // Build result: direct matches first (preserving order), then added siblings
    const result = [...directMatches]
    for (const id of resultIds) {
      if (!matchedIds.has(id) && allDisplayIds.has(id)) {
        result.push(allDisplayIds.get(id))
      }
    }

    return result
  }, [allAppsForDisplay, query, appGroups])

  // Sort filtered apps
  const sortedApps = useMemo(() => {
    const sorted = [...filteredApps]

    switch (sortBy) {
      case SORT_OPTIONS.EDITED_DESC:
        return sorted.sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        )
      case SORT_OPTIONS.NAME_ASC:
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      case SORT_OPTIONS.NAME_DESC:
        return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''))
      case SORT_OPTIONS.DATE_ASC:
        return sorted.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        )
      case SORT_OPTIONS.DATE_DESC:
      default:
        return sorted.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )
    }
  }, [filteredApps, sortBy])

  const handleDeleteApp = useCallback(
    async app => {
      const message = `Are you sure you want to delete ${app.name}?\n\nPlease type the app name to confirm:`
      const input = window.prompt(message)

      if (input === null) {
        return
      }

      if (input.toLowerCase().trim() !== app.name.toLowerCase().trim()) {
        toast.error(
          `App name doesn't match. Please type correctly to confirm deletion.`,
          {
            position: 'bottom-right',
            className: 'generic-error__toast',
            bodyClassName: 'generic-error__toast-body',
            hideProgressBar: true,
            autoClose: 5000,
          }
        )

        return
      }

      setDeletingAppId(app.id)

      try {
        await dispatch(deleteApp(app.id))
      } catch (error) {
        toast.error('Failed to delete app. Please try again.', {
          position: 'bottom-right',
          className: 'generic-error__toast',
          bodyClassName: 'generic-error__toast-body',
          hideProgressBar: true,
          autoClose: 5000,
        })
      } finally {
        setDeletingAppId(null)
      }
    },
    [dispatch]
  )

  const handleCopyApp = useCallback(app => {
    setCopyingApp(app)
  }, [])

  // Handle joining an app (for admins viewing non-member apps)
  const handleJoinApp = useCallback(
    async app => {
      if (!user?.id || !currentOrganization?.id) return

      await dispatch(addAppUser(app.id, user.id))
      // Refresh org apps to update membership status
      dispatch(requestOrgApps(currentOrganization.id))
    },
    [dispatch, user?.id, currentOrganization?.id]
  )

  const handleCopySubmit = useCallback(
    formValues => {
      if (!copyingApp) {
        return
      }

      submitCopyApp(copyingApp, formValues)
      setCopyingApp(null)
    },
    [copyingApp]
  )

  const handleCopyCancel = useCallback(() => {
    setCopyingApp(null)
  }, [])

  // Group sorted apps by shared database for display
  const groupedApps = useMemo(() => {
    const sortedIds = new Set(sortedApps.map(app => app.id))

    // If we have grouped data from orgApps, use it
    if (appGroups.length > 0) {
      const groups = appGroups
        .map(group => {
          // Filter to only include sorted apps, maintain sort order
          const filtered = group.filter(app => sortedIds.has(app.id))
          // Sort within group based on sortedApps order

          return filtered.sort((a, b) => {
            const aIndex = sortedApps.findIndex(app => app.id === a.id)
            const bIndex = sortedApps.findIndex(app => app.id === b.id)

            return aIndex - bIndex
          })
        })
        .filter(group => group.length > 0)

      if (groups.length > 0) {
        // Sort groups by the first app's position in sortedApps
        return groups.sort((a, b) => {
          const aIndex = sortedApps.findIndex(app => app.id === a[0].id)
          const bIndex = sortedApps.findIndex(app => app.id === b[0].id)

          return aIndex - bIndex
        })
      }
    }

    // Fallback: wrap each app in its own array (no grouping)

    return sortedApps.map(app => [app])
  }, [appGroups, sortedApps])

  return (
    <>
      {/* One-time welcome modal */}
      <NewDashboardModal />

      <div className="dashboard-page">
        <div className="dashboard-wrapper">
          <SettingsSidebar activeTab={activeTab} onTabChange={setActiveTab} />

          <div className="dashboard-content narrow">
            {activeTab !== 'apps' && (
              <button
                className="back-to-dashboard-link"
                onClick={() => setActiveTab('apps')}
              >
                ← Back to Dashboard
              </button>
            )}
            {activeTab === 'apps' ? (
              <div className="dashboard-apps-container">
                <div className="dashboard-apps-header">
                  <h3 className="settings-header">Apps</h3>

                  <div className="dashboard-apps-header-actions">
                    <div className="dashboard-search-input">
                      <i className="material-icons">search</i>
                      <input
                        type="text"
                        placeholder="Search Apps..."
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                      />
                    </div>
                    <MultiMenuTrigger
                      menu={[
                        {
                          label: 'Recently Edited',
                          value: SORT_OPTIONS.EDITED_DESC,
                        },
                        { label: '(A-Z)', value: SORT_OPTIONS.NAME_ASC },
                        { label: '(Z-A)', value: SORT_OPTIONS.NAME_DESC },
                        { label: 'New First', value: SORT_OPTIONS.DATE_DESC },
                        { label: 'Old First', value: SORT_OPTIONS.DATE_ASC },
                      ]}
                      onSelect={setSortBy}
                      rowHeight={32}
                    >
                      <Button
                        gray
                        small
                        outlined
                        icon="sort"
                        iconSide="right"
                        iconSize="small"
                      />
                    </MultiMenuTrigger>
                    {/* <Button
                      gray
                      small
                      outlined
                      icon={viewMode === VIEW_OPTIONS.GRID ? 'view_list' : 'grid_view'}
                      iconSize="small"
                      onClick={() =>
                        setViewMode(prev =>
                          prev === VIEW_OPTIONS.GRID
                            ? VIEW_OPTIONS.LIST
                            : VIEW_OPTIONS.GRID
                        )
                      }
                    /> */}
                  </div>
                </div>

                <div className="dashboard-apps-list-container">
                  <div
                    className={`dashboard-apps-list-inner app-list ${
                      viewMode === VIEW_OPTIONS.LIST ? 'app-list-compact' : ''
                    }`}
                  >
                    {/* New App tile for grid view */}
                    {viewMode === VIEW_OPTIONS.GRID && (
                      <a
                        href="/new"
                        className="app-item new-app-tile"
                        onClick={e => {
                          e.preventDefault()
                          setNewAppModalOpen(true)
                        }}
                      >
                        <img
                          src={addButtonIcon}
                          alt="New App"
                          className="dashboard-add-icon"
                        />
                        <h2 className="app-item-name">New App</h2>
                      </a>
                    )}
                    {!appsLoaded && (
                      <div style={{ padding: '80px 0' }}>
                        <Loading expanded />
                      </div>
                    )}
                    {appsLoaded &&
                      groupedApps.map(group => {
                        if (group.length > 1) {
                          // Shared database group
                          const hasPublishedApp = group.some(
                            app => app.published
                          )

                          return (
                            <div
                              key={group[0].id}
                              className="shared-database-group"
                            >
                              <div className="shared-database-header">
                                <span>Shared Database</span>
                                {hasPublishedApp && (
                                  <Icon
                                    type="done-all"
                                    color="green"
                                    iconStyle="circle"
                                    tooltip="Published! 🙌"
                                    placement="right"
                                  />
                                )}
                              </div>
                              <div className="shared-database-apps">
                                {group.map(app => (
                                  <AppItem
                                    key={app.id}
                                    app={app}
                                    compact={viewMode === VIEW_OPTIONS.LIST}
                                    isMuted={app._isMuted}
                                    onDelete={
                                      app._isMuted ? null : handleDeleteApp
                                    }
                                    onCopy={app._isMuted ? null : handleCopyApp}
                                    onJoin={app._isMuted ? handleJoinApp : null}
                                    isDeleting={deletingAppId === app.id}
                                  />
                                ))}
                              </div>
                            </div>
                          )
                        }

                        // Single app (may or may not have shared datasource)
                        const app = group[0]

                        return (
                          <AppItem
                            key={app.id}
                            app={app}
                            compact={viewMode === VIEW_OPTIONS.LIST}
                            isPublished={app.published}
                            isMuted={app._isMuted}
                            onDelete={app._isMuted ? null : handleDeleteApp}
                            onCopy={app._isMuted ? null : handleCopyApp}
                            onJoin={app._isMuted ? handleJoinApp : null}
                            isDeleting={deletingAppId === app.id}
                          />
                        )
                      })}
                  </div>
                  {/* Bottom button for list/compact view only */}
                  {viewMode === VIEW_OPTIONS.LIST && (
                    <a
                      href="/new"
                      className="dashboard-new-app-button"
                      onClick={e => {
                        e.preventDefault()
                        setNewAppModalOpen(true)
                      }}
                    >
                      <img src={addButtonIcon} alt="New App" />
                      <span>CREATE NEW APP</span>
                    </a>
                  )}
                </div>

                {isNewAppModalOpen ? (
                  <NewAppModal onClose={() => setNewAppModalOpen(false)} />
                ) : null}

                {copyingApp ? (
                  <Modal>
                    <CopyAppForm
                      key={copyingApp.id}
                      onSubmit={handleCopySubmit}
                      onCancel={handleCopyCancel}
                      initialValues={{
                        name: `${copyingApp.name} Copy`,
                        copyAsResponsive: false,
                        organizationId: copyingApp.OrganizationId,
                        copyDatabase: '1',
                        ...(copyingApp.primaryPlatform !== 'responsive'
                          ? { appType: 'legacy' }
                          : {}),
                      }}
                      isXanoApp={verifyXanoApp(copyingApp)}
                      isResponsiveApp={copyingApp?.magicLayout}
                      primaryPlatform={copyingApp?.primaryPlatform}
                      enableReinitialize
                    />
                  </Modal>
                ) : null}
              </div>
            ) : null}

            {/* Always mount components so they can dispatch their own data fetches */}
            {activeTab === 'account' && <Account />}
            {activeTab === 'team' && <Team />}
            {activeTab === 'billing' && <Billing />}
            {activeTab === 'domains' && <Domain />}

            {/* Overlay a spinner while user/org data is loading */}
            {activeTab !== 'apps' && !settingsLoaded && <Loading expanded />}
          </div>
        </div>
      </div>
    </>
  )
}

export default Dashboard
